<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useAirBulletin } from '../composables/useAirBulletin';
const {
  shareModalOpen,
  shareCardUrl,
  shareCardFilename,
  canNativeShare,
  currentShareTargets,
  triggerNativeShare,
  copyShareText,
  shareRegion,
  shareFormat,
  shareRegionOptions,
  sharePreparing,
  shareError,
  nativeSharePending,
  renderShareCard,
  toastVisible,
  toastMessage,
} = useAirBulletin();
const dialog = ref<HTMLDialogElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
let returnTarget: HTMLElement | null = null;
let originalOverflow: string | null = null;
watch(shareModalOpen, async (open) => {
  if (!open) {
    if (!dialog.value?.open) releaseDialog();
    return;
  }
  if (!dialog.value?.open) {
    returnTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  await nextTick();
  if (!shareModalOpen.value) return;
  if (!dialog.value?.open) dialog.value?.showModal();
  closeButton.value?.focus();
});
function releaseDialog() {
  dialog.value?.close();
  if (originalOverflow !== null) document.body.style.overflow = originalOverflow;
  originalOverflow = null;
  if (returnTarget?.isConnected) returnTarget.focus();
  returnTarget = null;
}
function finishClose() {
  if (!shareModalOpen.value) releaseDialog();
}
function close() {
  shareModalOpen.value = false;
}
function trapFocus(event: KeyboardEvent) {
  if (event.key !== 'Tab') return;
  const controls = dialog.value?.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], select:not([disabled])',
  );
  if (!controls?.length) return;
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
onBeforeUnmount(releaseDialog);
</script>
<template>
  <dialog
    ref="dialog"
    class="share-dialog"
    aria-labelledby="share-title"
    aria-modal="true"
    @cancel.prevent="close"
    @keydown.stop="trapFocus"
    @close="close"
  >
    <Transition name="modal" @after-leave="finishClose">
      <div
        v-if="shareModalOpen"
        id="share-card-modal"
        class="modal-overlay open"
        @click.self="close"
      >
        <section class="modal-box card-preview-box">
          <div class="modal-header">
            <div>
              <span class="section-kicker">PASS THE PUN ALONG</span>
              <h2 id="share-title">A little something to share.</h2>
            </div>
            <button
              id="close-share-modal-btn"
              ref="closeButton"
              class="close-btn"
              type="button"
              aria-label="Close dialog"
              @click="close"
            >
              ×
            </button>
          </div>
          <div class="share-options">
            <label
              >Reading<select v-model="shareRegion">
                <option
                  v-for="region in shareRegionOptions"
                  :key="region.value"
                  :value="region.value"
                  :disabled="region.disabled"
                >
                  {{ region.label }}
                </option>
              </select></label
            >
            <label
              >Image format<select v-model="shareFormat">
                <option value="portrait">Portrait · 1080 × 1350</option>
                <option value="square">Square · 1080 × 1080</option>
              </select></label
            >
          </div>
          <div
            class="card-preview-wrapper"
            :class="{ 'preview-pending': sharePreparing || shareError }"
            :aria-busy="sharePreparing"
            :style="{ aspectRatio: shareFormat === 'square' ? '1' : '4 / 5' }"
          >
            <p v-if="sharePreparing" class="share-preparing" role="status">Preparing your card…</p>
            <div v-else-if="shareError" class="share-preparing" role="alert">
              <p>{{ shareError }}</p>
              <button class="text-button" type="button" @click="renderShareCard">Try again</button>
            </div>
            <img
              v-else-if="shareCardUrl"
              id="share-card-img"
              class="share-card-img"
              :src="shareCardUrl"
              :width="1080"
              :height="shareFormat === 'square' ? 1080 : 1350"
              alt="HazePun card with the captured region, PSI, pun, matching Merlion, and reading time"
            />
          </div>
          <div class="share-modal-actions">
            <button
              v-if="canNativeShare"
              id="native-share-btn"
              class="button button-native"
              type="button"
              :disabled="sharePreparing || !shareCardUrl || nativeSharePending"
              @click="triggerNativeShare"
            >
              {{ nativeSharePending ? 'Opening share…' : 'Share directly' }}
              <span aria-hidden="true">↗</span>
            </button>
            <a
              v-if="shareCardUrl && !sharePreparing"
              id="download-card-link"
              class="button button-primary"
              :href="shareCardUrl"
              :download="shareCardFilename"
              >Download image <span aria-hidden="true">↓</span></a
            >
            <button
              id="copy-card-text-btn"
              class="button button-secondary"
              type="button"
              :disabled="sharePreparing"
              @click="copyShareText"
            >
              Copy caption
            </button>
          </div>
          <p class="share-feedback" role="status" aria-live="polite">
            {{ toastVisible ? toastMessage : '' }}
          </p>
          <div v-if="!sharePreparing" class="share-channels">
            <span class="share-channels-label">Or share to:</span>
            <div class="share-channels-list">
              <a
                v-for="target in currentShareTargets"
                :key="target.id"
                :href="target.href"
                target="_blank"
                rel="noopener"
                class="share-chip"
                :class="'share-chip-' + target.id"
                >{{ target.label }}</a
              >
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </dialog>
</template>
